import {
  Component,
  Inject,
  OnInit
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpErrorResponse} from '@angular/common/http';
import {first} from 'rxjs/operators';

import {CommissionTable} from '../../../../shared/interface/commission-table';
import {CommissionTableService} from '../../../../shared/services/commission-table.service';
import {FormHelperService} from '../../../../shared/services/form-helper.service';

export interface EditCommissionTableModalData {
  commissionTable: CommissionTable;
}

@Component({
  selector: 'app-edit-commission-table-modal',
  templateUrl: './edit-commission-table-modal.component.html',
  styleUrls: ['./edit-commission-table-modal.component.scss']
})
export class EditCommissionTableModalComponent
  implements OnInit {

  commissionTable: CommissionTable;

  commissionTableForm: FormGroup;

  nameControl: FormControl;
  shortNameControl: FormControl;
  validFromControl: FormControl;
  validToControl: FormControl;

  processingRequest = false;
  errors: any = {};

  constructor(
    public dialogRef:
    MatDialogRef<EditCommissionTableModalComponent>,

    @Inject(MAT_DIALOG_DATA)
    public data: EditCommissionTableModalData,

    private fb: FormBuilder,
    private commissionTableService: CommissionTableService,
    private formHelper: FormHelperService,
    private snackBar: MatSnackBar,
  ) {
    this.commissionTable = data.commissionTable;

    this.commissionTableForm = this.fb.group({
      name: this.nameControl = this.fb.control('', [Validators.required]),
      shortName: this.shortNameControl = this.fb.control('', [Validators.required]),
      validFrom: this.validFromControl = this.fb.control('', [Validators.required]),
      validTo: this.validToControl = this.fb.control(''),
      assignments: this.fb.array([], [Validators.minLength(1)])
    });
  }

  ngOnInit(): void {
    this.nameControl.setValue(this.commissionTable.name);
    this.shortNameControl.setValue(this.commissionTable.shortName);
    this.validFromControl.setValue(this.commissionTable.validFrom);
    this.validToControl.setValue(this.commissionTable.validTo || '');

    this.commissionTable.assignments
      .forEach(assignment => {
        this.assignments.push(
          this.fb.group({
            company: this.fb.control(assignment.company.id, [Validators.required]),
            contractType: this.fb.control(assignment.contractType.id, [Validators.required])
          })
        );
      });

    if (this.assignments.length === 0) {
      this.addAssignment();
    }
  }

  get assignments(): FormArray {
    return this.commissionTableForm.get('assignments') as FormArray;
  }

  addAssignment(): void {
    this.assignments.push(
      this.fb.group({
        company: this.fb.control('', [Validators.required]),
        contractType: this.fb.control('', [Validators.required])
      })
    );
  }

  removeAssignment(index: number): void {
    if (this.assignments.length <= 1) {
      return;
    }

    this.assignments.removeAt(index);
  }

  save(): void {
    if (!this.commissionTableForm.valid) {
      this.commissionTableForm.markAllAsTouched();
      return;
    }

    if (this.hasDuplicateAssignments()) {
      this.snackBar.open(
        'Ta sama firma i rodzaj umowy zostały dodane więcej niż raz',
        '',
        {duration: 4000}
      );

      return;
    }

    this.processingRequest = true;

    this.formHelper.removeBackendErrorState(
      this.commissionTableForm,
      this.errors
    );

    this.errors = {};

    this.commissionTableService
      .update(
        this.commissionTable.uuid,
        this.commissionTableForm.value
      )
      .pipe(first())
      .subscribe(
        () => {
          this.processingRequest = false;

          this.snackBar.open(
            'Pomyślnie zaktualizowano tabelę prowizyjną',
            '',
            {duration: 2000}
          );

          this.dialogRef.close(true);
        },
        (error: HttpErrorResponse) => {
          this.processingRequest = false;

          this.snackBar.open(
            'Podczas aktualizacji tabeli prowizyjnej wystąpił błąd',
            error.status.toString(),
            {duration: 3000}
          );

          if (error.status === 400) {
            this.errors = error.error.error.list;

            this.formHelper.setBackendErrorState(
              this.commissionTableForm,
              this.errors
            );
          }
        }
      );
  }

  closeDialog(): void {
    this.dialogRef.close(false);
  }

  hasDuplicateAssignments(): boolean {
    const values = this.assignments.getRawValue();

    const keys = values
      .filter(item => item.company && item.contractType)
      .map(item => `${item.company}:${item.contractType}`);

    return new Set(keys).size !== keys.length;
  }
}
